import { useRef, useEffect, useState } from 'react';
import { usePreviewStore } from '../../../stores/previewStore';
import { useStudioStore } from '../../../stores/studioStore';
import BackgroundRenderer from './BackgroundRenderer';
import OverlayRenderer from './OverlayRenderer';

export default function PreviewCanvas() {
  const {
    previewMode,
    resolvedElements,
    previewZoom,
    showBoundaries,
    showLayerIndicators,
  } = usePreviewStore();

  const { canvasState } = useStudioStore();
  const dimensions = canvasState.dimensions;
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const scaledWidth = dimensions.width * previewZoom;
  const scaledHeight = dimensions.height * previewZoom;

  const offsetX = Math.max(24, (containerSize.width - scaledWidth) / 2);
  const offsetY = Math.max(24, (containerSize.height - scaledHeight) / 2);

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-auto bg-[#0a0a0f]"
    >
      <div
        className="relative"
        style={{
          width: scaledWidth,
          height: scaledHeight,
          marginLeft: offsetX,
          marginTop: offsetY,
          marginBottom: Math.max(24, offsetY),
          marginRight: Math.max(24, offsetX),
        }}
      >
        <div
          className="absolute inset-0 rounded-sm"
          style={{
            boxShadow: '0 4px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.06)',
          }}
        />

        {(previewMode === 'background' || previewMode === 'combined') && (
          <div className="absolute inset-0">
            <BackgroundRenderer
              resolvedElements={resolvedElements}
              canvasWidth={dimensions.width}
              canvasHeight={dimensions.height}
              zoom={previewZoom}
              showBoundaries={showBoundaries}
              showLayerIndicators={showLayerIndicators}
            />
          </div>
        )}

        {(previewMode === 'overlay' || previewMode === 'combined') && (
          <div className="absolute inset-0">
            <OverlayRenderer
              resolvedElements={resolvedElements}
              canvasWidth={dimensions.width}
              canvasHeight={dimensions.height}
              zoom={previewZoom}
              showBoundaries={showBoundaries}
              showLayerIndicators={showLayerIndicators}
              transparent={previewMode === 'combined'}
            />
          </div>
        )}

        {previewMode === 'overlay' && !resolvedElements.some(
          (re) => re.resolved.layer === 'OVERLAY_LAYER' && re.resolved.visible
        ) && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <div className="text-center p-6 bg-[#1a1a2e]/90 rounded-xl border border-white/[0.06]">
              <p className="text-white/50 text-sm">No elements in OVERLAY_LAYER</p>
              <p className="text-white/30 text-xs mt-1">
                Assign text and interactive elements to the overlay layer for ADA compliance
              </p>
            </div>
          </div>
        )}

        {previewMode === 'background' && !resolvedElements.some(
          (re) => re.resolved.layer === 'BACKGROUND_LAYER' && re.resolved.visible
        ) && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <div className="text-center p-6 bg-[#1a1a2e]/90 rounded-xl border border-white/[0.06]">
              <p className="text-white/50 text-sm">No elements in BACKGROUND_LAYER</p>
              <p className="text-white/30 text-xs mt-1">
                Assign elements to the background layer in the editor
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
